import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { customDomains, type CustomDomain } from "@/api/admin";
import { Badge, Button, Card, CardBody, CardHeader, FormError, Input, Label } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";

export function AdminCustomDomainsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ["custom-domains"], queryFn: () => customDomains.list() });
  const [open, setOpen] = useState(false);
  const [showInstructionsFor, setShowInstructionsFor] = useState<CustomDomain | null>(null);

  const verify = useMutation({
    mutationFn: (domain: string) => customDomains.verify(domain),
    onSuccess: () => {
      toast.push("Domain verified", "success");
      qc.invalidateQueries({ queryKey: ["custom-domains"] });
    },
    onError: (e) => toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  const remove = useMutation({
    mutationFn: (domain: string) => customDomains.delete(domain),
    onSuccess: () => {
      toast.push("Domain removed", "success");
      qc.invalidateQueries({ queryKey: ["custom-domains"] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Custom domains</h1>
        <Button onClick={() => setOpen(true)}>Register domain</Button>
      </div>
      <Card>
        <CardBody className="overflow-x-auto">
          {list.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : list.data?.length === 0 ? (
            <p className="text-sm text-slate-500">
              No custom domains yet. Register one and we'll generate a DNS-validation token.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Domain</th>
                  <th>Status</th>
                  <th>SSL</th>
                  <th>Verified</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-2 font-medium">{d.domain}</td>
                    <td>{statusBadge(d.status)}</td>
                    <td>{statusBadge(d.ssl_status)}</td>
                    <td className="text-xs text-slate-500">{d.verified_at ? new Date(d.verified_at).toLocaleString() : "—"}</td>
                    <td>
                      <div className="flex justify-end gap-1">
                        {d.status !== "active" && (
                          <>
                            <Button variant="ghost" onClick={() => setShowInstructionsFor(d)}>
                              DNS instructions
                            </Button>
                            <Button onClick={() => verify.mutate(d.domain)} loading={verify.isPending}>
                              Verify
                            </Button>
                          </>
                        )}
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Remove ${d.domain}?`)) remove.mutate(d.domain);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <RegisterModal open={open} onClose={() => setOpen(false)} />
      <DnsInstructions
        domain={showInstructionsFor}
        onClose={() => setShowInstructionsFor(null)}
      />
    </div>
  );
}

function statusBadge(s: string) {
  const tone = s === "active" ? "good" : s === "suspended" ? "bad" : "warn";
  return <Badge tone={tone}>{s}</Badge>;
}

function RegisterModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [domain, setDomain] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = useMutation({
    mutationFn: (d: string) => customDomains.register(d),
    onSuccess: () => {
      toast.push("Domain registered. Add the DNS TXT record next.", "success");
      qc.invalidateQueries({ queryKey: ["custom-domains"] });
      onClose();
      setDomain("");
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });
  return (
    <Modal open={open} onClose={onClose} title="Register custom domain">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          submit.mutate(domain.trim());
        }}
        className="space-y-3"
      >
        <div>
          <Label>Domain</Label>
          <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="trade.acme.com" required />
          <p className="mt-1 text-xs text-slate-500">
            After registering, you'll get a DNS-validation TXT record. Once published, click <strong>Verify</strong>.
          </p>
        </div>
        <FormError>{error}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submit.isPending}>
            Register
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DnsInstructions({ domain, onClose }: { domain: CustomDomain | null; onClose: () => void }) {
  if (!domain) return null;
  return (
    <Modal open={true} onClose={onClose} title="DNS verification" size="lg">
      <div className="space-y-3 text-sm">
        <p>Publish this TXT record at your DNS host, then click Verify on the domains list.</p>
        <Card>
          <CardBody>
            <div className="grid grid-cols-3 gap-2 text-xs font-mono">
              <div className="text-slate-500">Type</div>
              <div className="col-span-2">TXT</div>
              <div className="text-slate-500">Name</div>
              <div className="col-span-2 break-all">_piggy-verify.{domain.domain}</div>
              <div className="text-slate-500">Value</div>
              <div className="col-span-2 break-all">{domain.dns_validation_token}</div>
            </div>
          </CardBody>
        </Card>
        <p className="text-xs text-slate-500">
          DNS propagation typically takes a few minutes. Most providers also let you set a TTL of
          300 seconds for faster turnaround.
        </p>
      </div>
    </Modal>
  );
}
